import React from "react";
import PropTypes from "prop-types";
import { useI18next } from "gatsby-plugin-react-i18next";
import data from "../../data/flavors.json";
import LinkGrid from "../LinkCard/LinkGrid.jsx";
import LinkCard from "../LinkCard/LinkCard.jsx";
import TagFilterMenu from "../LinkCard/TagFilterMenu.jsx";
import useTagFiltering from "../LinkCard/useTagFiltering.js";

const TAG_EMOJI_RULES = [
  { emoji: "🧺", keywords: ["marché", "market", "mercat"] },
  { emoji: "🛒", keywords: ["épicerie", "grocery", "alimentari", "buttegh"] },
  { emoji: "🥖", keywords: ["boulangerie", "bakery", "panetter"] },
  { emoji: "🥓", keywords: ["charcuterie", "cold cuts", "salumi"] },
  { emoji: "🍰", keywords: ["noisette", "douceur", "treat", "dolci"] },
  { emoji: "🍨", keywords: ["glacier", "ice‑cream", "ice cream", "gelaterie"] },
  { emoji: "🫒", keywords: ["huile", "olive", "aliva", "d'aliva"] },
  { emoji: "🍺", keywords: ["bière", "beer", "birra", "biere"] },
  {
    emoji: "🥃",
    keywords: ["spiritueux", "whisky", "distillerie", "distillery", "spirituos"],
  },
  { emoji: "🍷", keywords: ["domaine viticole", "vins", "vinicole", "wine", "vini"] },
  { emoji: "🧀", keywords: ["fromage", "cheese", "formagg", "casgi"] },
  { emoji: "🍯", keywords: ["miel", "honey", "miele"] },
  {
    emoji: "🧀",
    keywords: ["producteur", "produttore", "producer", "pruduttor"],
  },
];

const LocalFlavors = ({ limit, columns = 3, withFilterMenu = false }) => {
  const { language, defaultLanguage, t } = useI18next?.() || {};
  const lang = language || defaultLanguage || "fr";
  const fallbackLang = defaultLanguage || "fr";

  const mapExtraFields = React.useCallback(
    (entry, translated) => ({ emoji: entry.emoji || translated.emoji }),
    []
  );

  const {
    items,
    allTags,
    activeTag,
    setSelectedTag: setActiveTag,
    allLabel,
    tagCounts,
    totalCount,
  } = useTagFiltering({
    data,
    lang,
    fallbackLang,
    limit,
    translate: t,
    allLabelKey: "__Toutes",
    mapExtraFields,
  });

  const tagEmoji = (rawTag) => {
    if (!rawTag) return "";
    const raw = String(rawTag).trim().toLowerCase();
    const match = TAG_EMOJI_RULES.find(({ keywords }) =>
      keywords.some((keyword) => raw.includes(keyword))
    );
    return match?.emoji || "";
  };

  if (!items.length) return null;

  return (
    <>
      {withFilterMenu && allTags.length > 0 && (
        <TagFilterMenu
          allTags={allTags}
          activeTag={activeTag}
          setActiveTag={setActiveTag}
          allLabel={allLabel}
          ariaLabel={
            t?.("__Filtrer par type de saveur locale") ||
            "Filtrer par type de saveur locale"
          }
          tagEmoji={tagEmoji}
          tagCounts={tagCounts}
          totalCount={totalCount}
        />
      )}
      <LinkGrid columns={columns}>
        {items.map((e, idx) => (
          <LinkCard
            key={`${e.name || idx}|${e.website || e.mapsUrl || idx}`}
            title={e.name}
            href={e.website || e.mapsUrl || "#"}
            website={e.website}
            image={e.image}
            emoji={e.emoji}
            description={e.description}
            secondaryHref={e.mapsUrl}
            phone={e.phone}
          />
        ))}
      </LinkGrid>
    </>
  );
};

LocalFlavors.propTypes = {
  limit: PropTypes.number,
  columns: PropTypes.number,
  withFilterMenu: PropTypes.bool,
};

export default LocalFlavors;
